import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Search, ShoppingCart, Filter, ArrowUpDown, Grid, List as ListIcon } from "lucide-react"
import { toast } from "@/lib/toast"
import { useProducts, ProductFilters } from "@/hooks/useProducts"
import { useCart } from "@/hooks/useCart"
import { ProductResponseDto } from "@/api/api-client"
import { useQuery } from "@tanstack/react-query"
import { apiClient } from "@/api/client"
import { motion } from "framer-motion"
import { ConsumptionType } from "@/api/api-client"
import { CategoryFilterItem } from "./CategoryFilterItem"
import { CatalogSidebarFilter } from "./CatalogSidebarFilter"
import { ProductCardItem } from "./ProductCardItem"
import { CatalogPagination } from "./CatalogPagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CatalogFilters } from "./CatalogFilters"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"

const DEFAULT_PAGE_SIZE = 20

// Helper hook for debouncing multiple values
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

interface ProductCatalogProps {
  initialCategoryId?: number | null
  initialSearchTerm?: string
  consumptionType?: ConsumptionType
}

export const ProductCatalog = ({
  initialCategoryId = null,
  initialSearchTerm = "",
  consumptionType,
}: ProductCatalogProps) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
  const { t, language } = useLanguage()

  const content = t.productCatalog

  const [minPrice, setMinPrice] = useState("")
  const [maxPrice, setMaxPrice] = useState("")
  const [minWeight, setMinWeight] = useState("")
  const [maxWeight, setMaxWeight] = useState("")

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(initialCategoryId)
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([])
  const [sortBy, setSortBy] = useState<string>("name_asc")
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const debouncedMinPrice = useDebounce(minPrice, 500)
  const debouncedMaxPrice = useDebounce(maxPrice, 500)
  const debouncedMinWeight = useDebounce(minWeight, 500)
  const debouncedMaxWeight = useDebounce(maxWeight, 500)

  const filters: ProductFilters = {
    searchTerm: debouncedSearchTerm || undefined,
    categoryId: selectedCategoryId || undefined,
    providerIds: selectedProviderIds.length > 0 ? selectedProviderIds : undefined,
    sortBy: sortBy,
    minPrice: debouncedMinPrice ? parseFloat(debouncedMinPrice) : undefined,
    maxPrice: debouncedMaxPrice ? parseFloat(debouncedMaxPrice) : undefined,
    minWeight: debouncedMinWeight ? parseFloat(debouncedMinWeight) : undefined,
    maxWeight: debouncedMaxWeight ? parseFloat(debouncedMaxWeight) : undefined,
    page,
    pageSize,
    consumptionType,
  }

  const { data: productPage, isLoading, error } = useProducts(filters)
  const catalogProducts = productPage?.items ?? []
  const totalPageCount = productPage?.totalPageCount ?? 0

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiClient.getCategoryTree(),
  })

  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: () => apiClient.getAllProviders(),
  })

  const { addItem } = useCart()

  const handleSelectCategory = (id: number | null) => {
    setSelectedCategoryId(id)
    setPage(1)
  }

  const handleToggleProvider = (id: number | null) => {
    setPage(1)
    if (id === null) {
      setSelectedProviderIds([])
    } else {
      setSelectedProviderIds((prev) =>
        prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
      )
    }
  }

  const handleSortChange = (value: string) => {
    setSortBy(value)
    setPage(1)
  }

  const handlePageSizeChange = (value: number) => {
    setPageSize(value)
    setPage(1)
  }

  const handlePageChange = (value: number) => {
    setPage(value)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setPage(1)
  }

  const handleMinPriceChange = (value: string) => {
    setMinPrice(value)
    setPage(1)
  }

  const handleMaxPriceChange = (value: string) => {
    setMaxPrice(value)
    setPage(1)
  }

  const handleMinWeightChange = (value: string) => {
    setMinWeight(value)
    setPage(1)
  }

  const handleMaxWeightChange = (value: string) => {
    setMaxWeight(value)
    setPage(1)
  }

  const handleAddToCart = (product: ProductResponseDto) => {
    addItem(product as any)
    toast.success(`${product.name} ${content.addedToCart}`)
  }

  const handleContactForProduct = (productName: string) => {
    toast.info(`${content.contactInfo}: ${productName}`)
  }

  const resetFilters = () => {
    setSelectedCategoryId(null)
    setSelectedProviderIds([])
    setSearchTerm("")
    setMinPrice("")
    setMaxPrice("")
    setMinWeight("")
    setMaxWeight("")
    setPage(1)
  }

  return (
    <div className="flex flex-col gap-12 lg:flex-row">
      {/* Sidebar Filters */}
      <aside className="w-full shrink-0 lg:w-72">
        <div className="sticky top-28 space-y-8">
          <div className="mb-4 flex items-center justify-between lg:hidden">
            <h2 className="font-display text-2xl tracking-widest">{content.categories}</h2>
            <CatalogFilters label={content.categories}>
              <CatalogSidebarFilter
                categories={categories}
                providers={providers}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={handleSelectCategory}
                selectedProviderIds={selectedProviderIds}
                onToggleProvider={handleToggleProvider}
                minPrice={minPrice}
                maxPrice={maxPrice}
                onMinPriceChange={handleMinPriceChange}
                onMaxPriceChange={handleMaxPriceChange}
                minWeight={minWeight}
                maxWeight={maxWeight}
                onMinWeightChange={handleMinWeightChange}
                onMaxWeightChange={handleMaxWeightChange}
                language={language}
                text={{
                  categories: content.categories,
                  allCategories: content.allCategories,
                  providers: content.providers,
                  allProviders: content.allProviders,
                  priceRange: content.priceRange,
                  weightRange: content.weightRange,
                  min: content.min,
                  max: content.max,
                }}
              />
            </CatalogFilters>
          </div>

          <div className="hidden lg:block">
            <CatalogSidebarFilter
              categories={categories}
              providers={providers}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={handleSelectCategory}
              selectedProviderIds={selectedProviderIds}
              onToggleProvider={handleToggleProvider}
              minPrice={minPrice}
              maxPrice={maxPrice}
              onMinPriceChange={handleMinPriceChange}
              onMaxPriceChange={handleMaxPriceChange}
              minWeight={minWeight}
              maxWeight={maxWeight}
              onMinWeightChange={handleMinWeightChange}
              onMaxWeightChange={handleMaxWeightChange}
              language={language}
              text={{
                categories: content.categories,
                allCategories: content.allCategories,
                providers: content.providers,
                allProviders: content.allProviders,
                priceRange: content.priceRange,
                weightRange: content.weightRange,
                min: content.min,
                max: content.max,
              }}
            />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 space-y-8">
        {/* Top Header Row */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h1 className="industrial-font text-3xl uppercase tracking-tighter sm:text-4xl">
          </h1>
          <div className="flex items-center gap-4">
            {/* View Toggles */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-10 w-10 rounded-none border border-border/50",
                  viewMode === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
                )}
              >
                <Grid className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-10 w-10 rounded-none border border-border/50",
                  viewMode === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
                )}
              >
                <ListIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Sort */}
            <div className="w-48">
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="h-10 w-full rounded-none border-border/50 bg-background font-display text-lg tracking-widest transition-colors hover:border-primary focus:ring-0">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4 text-primary" />
                    <SelectValue placeholder={content.sort.label} />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-none border-border/50 bg-card">
                  <SelectItem value="name_asc" className="font-display tracking-wider">{content.sort.name_asc}</SelectItem>
                  <SelectItem value="price_asc" className="font-display tracking-wider">{content.sort.price_asc}</SelectItem>
                  <SelectItem value="price_desc" className="font-display tracking-wider">{content.sort.price_desc}</SelectItem>
                  <SelectItem value="newest" className="font-display tracking-wider">{content.sort.newest}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Filter / Search Bar Row */}
        <div className="group relative flex h-14 w-full items-center overflow-hidden border border-border/50 bg-background transition-colors hover:border-primary">
          <div className="absolute bottom-0 left-0 top-0 w-1 bg-primary transition-all duration-300 group-focus-within:w-1.5" />
          <Search className="ml-6 mr-3 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-primary" />
          <Input
            placeholder={content.search}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-full w-full rounded-none border-0 bg-transparent px-0 font-display text-xl tracking-[0.1em] shadow-none placeholder:text-muted-foreground/40 focus-visible:ring-0"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSearchChange("")}
              className="mr-2 h-auto text-xs font-black uppercase tracking-widest text-muted-foreground hover:bg-transparent hover:text-destructive"
            >
              {language === "es" ? "[ LIMPIAR ]" : "[ CLEAR ]"}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="relative aspect-[4/5] overflow-hidden rounded-lg border-[1px] border-border bg-muted/20 shadow-sm"
              >
                <div className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-accent/10" />
                <div className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-accent/10" />
                <div className="animate-scan absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-32 w-32 animate-pulse bg-foreground/5" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="border border-destructive/10 bg-destructive/5 py-20 text-center">
            <p className="font-display text-2xl tracking-widest text-destructive">{content.error}</p>
            <p className="mt-2 text-sm text-muted-foreground">{(error as Error).message}</p>
          </div>
        ) : catalogProducts.length > 0 ? (
          <>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 },
                },
              }}
              className={cn(
                "grid gap-x-6 gap-y-12",
                viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"
              )}
            >
              {catalogProducts.map((product) => (
                <ProductCardItem
                  key={product.id}
                  product={product}
                  viewMode={viewMode}
                  onAddToCart={handleAddToCart}
                  onContact={handleContactForProduct}
                  text={{
                    price: content.price,
                    contact: content.contact,
                    addToCart: content.addToCart,
                  }}
                />
              ))}
            </motion.div>

            <CatalogPagination
              page={page}
              pageSize={pageSize}
              totalPageCount={totalPageCount}
              isLoading={isLoading}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              text={content.pagination}
            />
          </>
        ) : page > 1 ? (
          <div className="empty-state py-32">
            <Filter className="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
            <p className="font-display text-2xl tracking-widest text-muted-foreground">
              {content.noPageResults}
            </p>
            <Button
              variant="link"
              onClick={() => handlePageChange(page - 1)}
              className="mt-2 font-display tracking-widest text-primary hover:text-primary/80"
            >
              {content.pagination.previous}
            </Button>
          </div>
        ) : (
          <div className="empty-state py-32">
            <Filter className="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
            <p className="font-display text-2xl tracking-widest text-muted-foreground">
              {content.noResults}
            </p>
            <Button
              variant="link"
              onClick={resetFilters}
              className="mt-2 font-display tracking-widest text-primary hover:text-primary/80"
            >
              {content.allCategories}
            </Button>
          </div>
        )}
      </div>
    </div >
  )
}

